import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import mesApi from "../../Api/mes.api";
import SearchableSelect from "../DataEntry/SearchableSelect";
import {
  BatchMaterialRow,
  BatchMaterialMissingRow,
  IngredientCandidateLot,
  Ingredient,
} from "../../types/mes";

/**
 * Sirovine zamjesa na ekranu izvršenja (npr. nalog za miksanje).
 *
 * Planiranje potvrdi lot po sirovini, ali se u pogonu redovno dogodi izmjena na
 * licu mjesta: drugi lot, druga količina, a ponekad i druga sirovina. Zato je
 * ovdje svaka nepotvrđena stavka uređiva (sirovina / količina / lot), a utrošak
 * se knjiži tek na „Potvrdi utrošak" — tada se skida zaliha sa skladišta
 * proizvodnje i stavka se zaključa. Ispravak nakon potvrde ide preko
 * „Poništi", koje stornira knjiženje protuknjižbom.
 *
 * Zamjena sirovine i stavka izvan recepture zahtijevaju obrazloženje (backend to
 * odbija bez njega) — bez toga se poslije ne vidi zašto je zamjesa odstupila.
 */

interface Props {
  batchId: number;
  /** Osvježi nadređeni ekran nakon potvrde (npr. trošak / status). */
  onChanged?: () => void;
}

interface RowEdit {
  ingredientId: number;
  materialLotId: number;
  qty: string;
  notes: string;
}

interface DraftLine {
  ingredientId: number;
  materialLotId: number;
  qty: string;
  notes: string;
}

const emptyDraft: DraftLine = { ingredientId: 0, materialLotId: 0, qty: "", notes: "" };

/** Količine se tipkaju i sa zarezom i s točkom (hrvatska tipkovnica / numpad). */
const parseQty = (value: string): number => Number(String(value).replace(",", "."));

const fmt = (value: number | null | undefined, decimals = 3): string =>
  value == null ? "—" : value.toFixed(decimals);

const lotLabel = (lot: IngredientCandidateLot): string => {
  const name = lot.lotNumber || `Lot #${lot.materialLotId}`;
  const supplier = lot.supplier ? ` · ${lot.supplier}` : "";
  const expiry = lot.expiryDate ? ` · rok ${new Date(lot.expiryDate).toLocaleDateString("hr-HR")}` : "";
  return `${name}${supplier} — stanje ${lot.remaining.toFixed(2)}${expiry}`;
};

/**
 * Katalog sirovina je isti za sve kartice zamjesa i mijenja se rijetko, a
 * kartice se otvaraju/zatvaraju stalno — bez ovog keša svako otvaranje naloga
 * povuče cijeli popis ispočetka.
 */
let ingredientCache: Promise<Ingredient[]> | null = null;
const loadIngredients = (): Promise<Ingredient[]> => {
  if (!ingredientCache) {
    ingredientCache = mesApi.getIngredients({ is_active: true }).catch((err) => {
      ingredientCache = null;
      throw err;
    });
  }
  return ingredientCache;
};

export default function BatchMaterialConfirm({ batchId, onChanged }: Props) {
  const [rows, setRows] = useState<BatchMaterialRow[]>([]);
  const [missing, setMissing] = useState<BatchMaterialMissingRow[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [lotsByIngredient, setLotsByIngredient] = useState<Record<number, IngredientCandidateLot[]>>({});
  const [edits, setEdits] = useState<Record<number, RowEdit>>({});
  const [draft, setDraft] = useState<DraftLine>(emptyDraft);
  const [showDraft, setShowDraft] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await mesApi.getBatchMaterials(batchId);
      setRows(data.rows);
      setMissing(data.missing);
      setEdits({});
    } catch (e: any) {
      toast.error(e?.message || "Greška pri dohvatu sirovina zamjesa");
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadIngredients()
      .then(setIngredients)
      .catch(() => {
        /* tiho — zamjena sirovine ostaje nedostupna, ostalo radi */
      });
  }, []);

  const ingredientOptions = useMemo(
    () =>
      ingredients.map((i) => ({
        value: i.ID,
        label: i.IngredientName,
        sublabel: i.IngredientTypeName || undefined,
      })),
    [ingredients]
  );

  /** Lotovi jedne sirovine — dohvat na prvo otvaranje izbornika. */
  const ensureLots = useCallback(
    async (ingredientId: number | null) => {
      if (ingredientId == null || ingredientId <= 0 || lotsByIngredient[ingredientId]) return;
      try {
        const lots = await mesApi.getIngredientCandidateLots(ingredientId);
        setLotsByIngredient((prev) => ({ ...prev, [ingredientId]: lots }));
      } catch {
        /* tiho — ostaje trenutno dodijeljeni lot */
      }
    },
    [lotsByIngredient]
  );

  const baseEdit = (row: BatchMaterialRow): RowEdit => ({
    ingredientId: row.ingredientId ?? 0,
    materialLotId: row.materialLotId ?? 0,
    qty: row.qty != null ? String(row.qty) : "",
    notes: row.notes || "",
  });

  const editFor = (row: BatchMaterialRow): RowEdit => edits[row.ID] ?? baseEdit(row);

  const patchEdit = (row: BatchMaterialRow, patch: Partial<RowEdit>) => {
    // Bazira se na `prev`, ne na `edits` iz zatvorenja: dvije izmjene u istom
    // React batchu (npr. sirovina + lot) inače krenu od istog starog stanja i
    // druga pregazi prvu.
    setEdits((prev) => ({ ...prev, [row.ID]: { ...(prev[row.ID] ?? baseEdit(row)), ...patch } }));
  };

  const isDirty = (row: BatchMaterialRow): boolean => {
    const e = edits[row.ID];
    if (!e) return false;
    return (
      e.ingredientId !== (row.ingredientId ?? 0) ||
      e.materialLotId !== (row.materialLotId ?? 0) ||
      parseQty(e.qty) !== row.qty ||
      (e.notes || "") !== (row.notes || "")
    );
  };

  const saveRow = async (row: BatchMaterialRow) => {
    const e = editFor(row);
    const qty = parseQty(e.qty);
    if (!(qty > 0)) {
      toast.error("Količina mora biti veća od 0");
      return;
    }
    const ingredientChanged = e.ingredientId !== (row.ingredientId ?? 0);

    setBusy(true);
    try {
      await mesApi.updateBatchMaterial(row.ID, {
        // Sirovinu šaljemo samo kad se mijenja: backend uz promjenu traži i novi
        // lot i obrazloženje, pa je ne smijemo poslati "za svaki slučaj".
        ...(ingredientChanged ? { ingredientId: e.ingredientId } : {}),
        materialLotId: e.materialLotId || null,
        qty,
        notes: e.notes || null,
      });
      toast.success("Sirovina ažurirana");
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Izmjena nije uspjela");
    } finally {
      setBusy(false);
    }
  };

  const removeRow = async (row: BatchMaterialRow) => {
    setBusy(true);
    try {
      await mesApi.deleteBatchMaterial(row.ID);
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Uklanjanje nije uspjelo");
    } finally {
      setBusy(false);
    }
  };

  const unconfirmRow = async (row: BatchMaterialRow) => {
    setBusy(true);
    try {
      await mesApi.unconfirmBatchMaterial(row.ID);
      toast.info("Potvrda poništena — stavka je ponovno uređiva");
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.message || "Poništenje nije uspjelo");
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    setBusy(true);
    try {
      const r = await mesApi.proposeBatchMaterials(batchId);
      if (r.created === 0 && r.shortages.length === 0) toast.info("Nema stavki za predlaganje");
      if (r.shortages.length > 0) {
        toast.warn(`Nedostaje zaliha: ${r.shortages.map((s) => s.material).join(", ")}`);
      }
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Prijedlog nije uspio");
    } finally {
      setBusy(false);
    }
  };

  const addDraft = async () => {
    const qty = parseQty(draft.qty);
    if (!draft.ingredientId) return toast.error("Odaberite sirovinu");
    if (!draft.materialLotId) return toast.error("Odaberite lot");
    if (!(qty > 0)) return toast.error("Unesite količinu");

    setBusy(true);
    try {
      await mesApi.addBatchMaterial(batchId, {
        ingredientId: draft.ingredientId,
        materialLotId: draft.materialLotId,
        qty,
        notes: draft.notes || null,
      });
      toast.success("Sirovina dodana");
      setDraft(emptyDraft);
      setShowDraft(false);
      await load();
    } catch (err: any) {
      toast.error(err?.message || "Dodavanje nije uspjelo");
    } finally {
      setBusy(false);
    }
  };

  const confirmAll = async () => {
    const dirty = rows.filter((r) => !r.confirmed && isDirty(r));
    if (dirty.length > 0) {
      toast.error("Spremite izmijenjene stavke prije potvrde");
      return;
    }
    setBusy(true);
    try {
      const r = await mesApi.confirmBatchMaterials(batchId);
      toast.success(`Potvrđeno stavki: ${r.confirmed}`);
      await load();
      onChanged?.();
    } catch (err: any) {
      toast.error(err?.message || "Potvrda nije uspjela");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="batch-materials batch-materials--empty">Učitavanje sirovina…</div>;
  }

  if (rows.length === 0 && missing.length === 0) {
    return (
      <div className="batch-materials batch-materials--empty">
        <span>Nema recepture ni evidentiranih sirovina za ovu zamjesu.</span>
      </div>
    );
  }

  const unconfirmed = rows.filter((r) => !r.confirmed);
  const totalQty = rows.reduce((s, r) => s + (r.qty || 0), 0);
  const totalCost = rows.reduce((s, r) => s + (r.cost || 0), 0);

  const deviation = (row: BatchMaterialRow): string => {
    if (row.recipeQty == null || row.recipeQty === 0) return "";
    const delta = row.qty - row.recipeQty;
    if (Math.abs(delta) < 0.001) return "";
    const pct = (delta / row.recipeQty) * 100;
    return `${delta > 0 ? "+" : ""}${delta.toFixed(3)} kg (${pct > 0 ? "+" : ""}${pct.toFixed(1)}%)`;
  };

  return (
    <div className="batch-materials">
      <div className="batch-materials__head">
        <span className="batch-materials__title">Sirovine u zamjes</span>
        <span className="batch-materials__total">
          {fmt(totalQty, 2)} kg{totalCost > 0 ? ` · trošak ${totalCost.toFixed(2)}` : ""}
        </span>
      </div>

      <div className="mes-table-wrap">
        <table className="batch-materials__table">
          <thead>
            <tr>
              <th style={{ minWidth: 180 }}>Sirovina</th>
              <th style={{ width: 90 }}>Recept (kg)</th>
              <th style={{ width: 110 }}>Količina (kg)</th>
              <th style={{ minWidth: 180 }}>Lot</th>
              <th style={{ minWidth: 140 }}>Napomena / razlog</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 150 }}>Akcije</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const e = editFor(row);
              const lots = lotsByIngredient[e.ingredientId] || [];
              const dirty = isDirty(row);
              const dev = deviation(row);

              if (row.confirmed) {
                return (
                  <tr key={row.ID} className="is-confirmed">
                    <td>
                      {row.material}
                      {row.offRecipe && <span className="batch-materials__flag">izvan recepture</span>}
                    </td>
                    <td className="num">{row.recipeQty == null ? "—" : fmt(row.recipeQty)}</td>
                    <td className="num">
                      {fmt(row.qty)}
                      {dev && <span className="batch-materials__dev">{dev}</span>}
                    </td>
                    <td>{row.lotNumber || (row.materialLotId ? `Lot #${row.materialLotId}` : "—")}</td>
                    <td>{row.notes || "—"}</td>
                    <td>
                      <span className="batch-materials__badge ok">Potvrđeno</span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => unconfirmRow(row)}
                      >
                        Poništi
                      </button>
                    </td>
                  </tr>
                );
              }

              return (
                <tr key={row.ID}>
                  <td>
                    {/* Poluproizvod dolazi iz vlastite zamjese — ne zamjenjuje se. */}
                    {row.kind === "semi_finished" || !row.editableLot ? (
                      <>
                        {row.material}
                        <span className="batch-materials__flag">poluproizvod</span>
                      </>
                    ) : (
                      <SearchableSelect
                        value={e.ingredientId}
                        options={ingredientOptions}
                        disabled={busy}
                        allowClear={false}
                        placeholder="Pretraži sirovinu…"
                        onChange={(v) => {
                          // Nova sirovina znači i novi lot — stari pripada staroj.
                          patchEdit(row, { ingredientId: v, materialLotId: 0 });
                          ensureLots(v);
                        }}
                      />
                    )}
                    {row.offRecipe && <span className="batch-materials__flag">izvan recepture</span>}
                  </td>
                  <td className="num">{row.recipeQty == null ? "—" : fmt(row.recipeQty)}</td>
                  <td>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="batch-materials__input"
                      value={e.qty}
                      disabled={busy}
                      onChange={(ev) => patchEdit(row, { qty: ev.target.value })}
                    />
                    {dev && <span className="batch-materials__dev">{dev}</span>}
                  </td>
                  <td>
                    {row.editableLot ? (
                      <select
                        className="batch-materials__select"
                        value={e.materialLotId || ""}
                        disabled={busy}
                        onFocus={() => ensureLots(e.ingredientId)}
                        onChange={(ev) => patchEdit(row, { materialLotId: parseInt(ev.target.value, 10) || 0 })}
                      >
                        <option value="">— odaberi lot —</option>
                        {/* Dodijeljeni lot ostaje u ponudi i kad ga popis kandidata
                            ne vrati (npr. potrošen do nule na skladištu). */}
                        {e.materialLotId > 0 &&
                          !lots.some((l) => l.materialLotId === e.materialLotId) && (
                            <option value={e.materialLotId}>
                              {row.lotNumber || `Lot #${e.materialLotId}`}
                            </option>
                          )}
                        {lots.map((l) => (
                          <option key={l.materialLotId} value={l.materialLotId}>
                            {lotLabel(l)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      row.lotNumber || "—"
                    )}
                  </td>
                  <td>
                    <input
                      type="text"
                      className="batch-materials__input"
                      placeholder={e.ingredientId !== (row.ingredientId ?? 0) ? "Razlog zamjene (obavezno)" : "Razlog…"}
                      value={e.notes}
                      disabled={busy}
                      onChange={(ev) => patchEdit(row, { notes: ev.target.value })}
                    />
                  </td>
                  <td>
                    <span className={`batch-materials__badge ${dirty ? "warn" : ""}`}>
                      {dirty ? "Nespremljeno" : row.offRecipe ? "Zamjena" : "Prijedlog"}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-edit btn-sm"
                      disabled={busy || !dirty}
                      onClick={() => saveRow(row)}
                    >
                      Spremi
                    </button>
                    <button
                      type="button"
                      className="btn-delete btn-sm"
                      disabled={busy}
                      onClick={() => removeRow(row)}
                    >
                      Ukloni
                    </button>
                  </td>
                </tr>
              );
            })}

            {/* Recepturne stavke bez dodijeljenog lota. */}
            {missing.map((m) => (
              <tr key={m.key} className="is-missing">
                <td>
                  {m.material}
                  {!m.tracksLots && <span className="batch-materials__flag">bez lota</span>}
                </td>
                <td className="num">{fmt(m.recipeQty)}</td>
                <td className="num">—</td>
                <td colSpan={2}>
                  {m.tracksLots ? "Lot nije dodijeljen" : "Ne vodi se po lotu (voda / poluproizvod)"}
                </td>
                <td>
                  <span className={`batch-materials__badge ${m.tracksLots ? "warn" : ""}`}>
                    {m.tracksLots ? "Nedodijeljeno" : "Info"}
                  </span>
                </td>
                <td />
              </tr>
            ))}

            {showDraft && (
              <tr className="is-draft">
                <td>
                  <SearchableSelect
                    value={draft.ingredientId}
                    options={ingredientOptions}
                    disabled={busy}
                    placeholder="Pretraži sirovinu…"
                    onChange={(v) => {
                      setDraft({ ...draft, ingredientId: v, materialLotId: 0 });
                      ensureLots(v);
                    }}
                  />
                </td>
                <td className="num">—</td>
                <td>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="batch-materials__input"
                    value={draft.qty}
                    disabled={busy}
                    onChange={(ev) => setDraft({ ...draft, qty: ev.target.value })}
                  />
                </td>
                <td>
                  <select
                    className="batch-materials__select"
                    value={draft.materialLotId || ""}
                    disabled={busy || !draft.ingredientId}
                    onFocus={() => ensureLots(draft.ingredientId)}
                    onChange={(ev) => setDraft({ ...draft, materialLotId: parseInt(ev.target.value, 10) || 0 })}
                  >
                    <option value="">— odaberi lot —</option>
                    {(lotsByIngredient[draft.ingredientId] || []).map((l) => (
                      <option key={l.materialLotId} value={l.materialLotId}>
                        {lotLabel(l)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="text"
                    className="batch-materials__input"
                    placeholder="Razlog (obavezno izvan recepture)"
                    value={draft.notes}
                    disabled={busy}
                    onChange={(ev) => setDraft({ ...draft, notes: ev.target.value })}
                  />
                </td>
                <td>
                  <span className="batch-materials__badge warn">Nova</span>
                </td>
                <td>
                  <button type="button" className="btn-submit btn-sm" disabled={busy} onClick={addDraft}>
                    Dodaj
                  </button>
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    disabled={busy}
                    onClick={() => {
                      setDraft(emptyDraft);
                      setShowDraft(false);
                    }}
                  >
                    Odustani
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="batch-materials__actions">
        {missing.some((m) => m.tracksLots) && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={propose}>
            Predloži lotove (FEFO)
          </button>
        )}
        {!showDraft && (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => setShowDraft(true)}>
            + Dodaj sirovinu
          </button>
        )}
        {unconfirmed.length > 0 && (
          <button type="button" className="btn-submit" disabled={busy} onClick={confirmAll}>
            {busy ? "Spremanje…" : `Potvrdi utrošak (${unconfirmed.length})`}
          </button>
        )}
      </div>
      <p className="batch-materials__hint">
        Potvrda knjiži izdatak sa skladišta proizvodnje i zaključa stavku. Izmjena sirovine ili
        stavka izvan recepture zahtijevaju obrazloženje.
      </p>
    </div>
  );
}
